import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Divider,
  Popover,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguageAwareRender } from '../../hooks/useLanguageAwareRender';
import { t } from '../../utils/localization/localization';
import { logger } from '../../utils/logger';
import { ErrorState, tableShellSx, responsiveTableContainerSx } from '../common';
import departmentService from '../../services/departmentService';

export default function UserManagement() {
  const { ROLES, assignManager, getUserManager, getUserSubordinates, updateUser, deleteUser } = useAuth();

  // Use custom hook for language-aware rendering
  const languageRenderKey = useLanguageAwareRender('UserManagement');

  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add', 'edit', or 'manager'
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: ROLES.USER,
    managerId: '',
    department: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userManagers, setUserManagers] = useState({});
  const [userSubordinates, setUserSubordinates] = useState({});
  const [departmentOptions, setDepartmentOptions] = useState([]);

  // Filter states (following CV/Job analyzer pattern)
  const [roleFilter, setRoleFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [roleFilterAnchorEl, setRoleFilterAnchorEl] = useState(null);
  const [managerFilterAnchorEl, setManagerFilterAnchorEl] = useState(null);
  const [departmentFilterAnchorEl, setDepartmentFilterAnchorEl] = useState(null);

  const userTableColumnSx = {
    name: { width: { xs: '16%', md: '16%' }, minWidth: { xs: 130, md: 160 } },
    email: { width: { xs: '23%', md: '22%' }, minWidth: { xs: 190, md: 220 } },
    role: { width: { xs: '11%', md: '11%' }, minWidth: { xs: 100, md: 120 }, whiteSpace: 'nowrap' },
    manager: { width: { xs: '14%', md: '14%' }, minWidth: { xs: 120, md: 140 } },
    department: { width: { xs: '14%', md: '14%' }, minWidth: { xs: 120, md: 140 } },
    subordinates: { width: { xs: '10%', md: '10%' }, minWidth: { xs: 100, md: 120 }, whiteSpace: 'nowrap' },
    actions: { width: { xs: '1%', md: '13%' }, minWidth: 130, whiteSpace: 'nowrap' }
  };

  // Get role display name
  const getRoleDisplayName = (role) => {
    switch(role) {
      case ROLES.ADMIN:
        return t('userManagement.roleAdmin');
      case ROLES.MANAGER:
        return t('userManagement.roleManager');
      case ROLES.ANALYST:
        return t('userManagement.roleAnalyst');
      case ROLES.USER:
        return t('userManagement.roleUser');
      default:
        return t('userManagement.roleUnknown');
    }
  };

  // Get role color
  const getRoleColor = (role) => {
    switch(role) {
      case ROLES.ADMIN:
        return 'error';
      case ROLES.MANAGER:
        return 'warning';
      case ROLES.ANALYST:
        return 'info';
      case ROLES.USER:
        return 'success';
      default:
        return 'default';
    }
  };

  // Load real users from the auth context
  const { getAllUsers, updateUserRole: _updateUserRole } = useAuth();

  // Load department options
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const options = await departmentService.getDepartmentOptions(true);
        logger.info('[USER_MANAGEMENT] Loaded department options:', options);
        setDepartmentOptions(options);
      } catch (error) {
        logger.error('[USER_MANAGEMENT] Error loading department options:', error);
        setDepartmentOptions([]);
        setError(error.message || t('errors.genericError'));
      }
    };

    loadDepartments();
  }, []);

  // Load users
  useEffect(() => {
    // Get all users from the auth context
    const loadUsers = async () => {
      try {
        // getAllUsers is an async function, so we need to await it
        const allUsers = await getAllUsers();
        logger.info('[USER_MANAGEMENT] Loaded users:', allUsers);
        setUsers(allUsers || []);

        // Load manager and subordinate info for each user
        const managerMap = {};
        const subordinatesMap = {};

        if (Array.isArray(allUsers)) {
          for (const user of allUsers) {
            try {
              const manager = await getUserManager(user.id);
              const subordinates = await getUserSubordinates(user.id);

              // Ensure manager is an object or null
              managerMap[user.id] = manager || null;

              // Ensure subordinates is always an array
              subordinatesMap[user.id] = Array.isArray(subordinates) ? subordinates : [];

              logger.info(`[USER_MANAGEMENT] User ${user.id} has manager: ${manager?.name || 'none'} and ${subordinatesMap[user.id].length} subordinates`);
            } catch (error) {
              logger.error(`[USER_MANAGEMENT] Error loading hierarchy for user ${user.id}:`, error);
              throw error;
            }
          }
        }

        setUserManagers(managerMap);
        setUserSubordinates(subordinatesMap);
      } catch (error) {
        logger.error('[USER_MANAGEMENT] Error loading users:', error);
      }
    };

    loadUsers();

    // Set up an interval to refresh the user list every 5 seconds
    const interval = setInterval(() => {
      loadUsers();
    }, 5000);

    return () => clearInterval(interval);
  }, [getAllUsers, getUserManager, getUserSubordinates]);

  const handleOpenDialog = (mode, user = null) => {
    setDialogMode(mode);
    setSelectedUser(user);

    if (mode === 'edit' && user) {
      // Find the department ID from the department name
      const departmentOption = departmentOptions.find(opt => opt.label === user.department);
      const departmentId = departmentOption ? departmentOption.value : (user.department ? '' : '');

      setFormData({
        name: user.name,
        email: user.email,
        password: '', // Don't populate password for security
        role: user.role,
        managerId: user.managerId || '',
        department: departmentId || ''
      });
    } else if (mode === 'manager' && user) {
      setFormData({
        ...formData,
        managerId: user.managerId || ''
      });
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (dialogMode === 'manager') {
        // Update user's manager
        try {
          const result = await assignManager(selectedUser.id, formData.managerId || null);
          if (result.success) {
            setSuccess('Manager assigned successfully');

            // Refresh the user list
            const allUsers = await getAllUsers();
            setUsers(allUsers || []);

            // Update manager and subordinate info
            const managerMap = {};
            const subordinatesMap = {};

            if (Array.isArray(allUsers)) {
              for (const user of allUsers) {
                try {
                  const manager = await getUserManager(user.id);
                  const subordinates = await getUserSubordinates(user.id);

                  managerMap[user.id] = manager || null;
                  subordinatesMap[user.id] = Array.isArray(subordinates) ? subordinates : [];
                } catch (error) {
                  logger.error(`[USER_MANAGEMENT] Error loading hierarchy for user ${user.id}:`, error);
                  throw error;
                }
              }
            }

            setUserManagers(managerMap);
            setUserSubordinates(subordinatesMap);

            logger.info('[USER_MANAGEMENT]', 'Manager assigned successfully and UI refreshed');
          }
        } catch (error) {
          setError('Failed to assign manager: ' + error.message);
          return;
        }
      } else if (dialogMode === 'edit') {
        // Validate form
        if (!formData.name || !formData.email) {
          setError('Please fill in all required fields');
          return;
        }

        // Update user (name, email, role, and department)
        try {
          // Convert department ID back to department name for API
          const departmentName = formData.department ?
            departmentOptions.find(opt => opt.value === formData.department)?.label || '' : '';

          const result = await updateUser(
            selectedUser.id,
            formData.name,
            formData.email,
            formData.role,
            departmentName
          );
          if (result.success) {
            setSuccess('User updated successfully');

            // Refresh the user list
            const allUsers = await getAllUsers();
            setUsers(allUsers || []);

            logger.info('[USER_MANAGEMENT]', 'User updated successfully and UI refreshed');
          }
        } catch (error) {
          setError(t('userManagement.failedToUpdateUser') + ': ' + error.message);
          return;
        }
      }

      handleCloseDialog();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      setError('Error: ' + error.message);
    }
  };

  // Filter users based on role, manager, and department filters
  const getFilteredUsers = () => {
    return users.filter(user => {
      // Role filter
      if (roleFilter && user.role !== roleFilter) {
        return false;
      }

      // Manager filter
      if (managerFilter) {
        const userManager = userManagers[user.id];
        if (managerFilter === 'none') {
          // Filter for users with no manager
          if (userManager) return false;
        } else {
          // Filter for users with specific manager
          if (!userManager || userManager.id !== managerFilter) return false;
        }
      }

      // Department filter
      if (departmentFilter) {
        if (departmentFilter === 'none') {
          // Filter for users with no department
          if (user.department && user.department.trim() !== '') return false;
        } else {
          // Filter for users with specific department name
          if (user.department !== departmentFilter) return false;
        }
      }

      return true;
    });
  };

  // Filter handlers (following CV/Job analyzer pattern)
  const handleRoleFilterClose = () => {
    setRoleFilterAnchorEl(null);
  };

  const handleManagerFilterClose = () => {
    setManagerFilterAnchorEl(null);
  };

  const handleDepartmentFilterClose = () => {
    setDepartmentFilterAnchorEl(null);
  };

  const handleRoleFilterSelect = (value) => {
    setRoleFilter(value);
    setRoleFilterAnchorEl(null);
  };

  const handleManagerFilterSelect = (value) => {
    setManagerFilter(value);
    setManagerFilterAnchorEl(null);
  };

  const handleDepartmentFilterSelect = (value) => {
    setDepartmentFilter(value);
    setDepartmentFilterAnchorEl(null);
  };

  // Get active departments for filtering
  const getActiveDepartments = () => {
    return departmentOptions
      .filter(option => option.value !== null)
      .map(option => option.label)
      .sort();
  };

  const handleDeleteUser = async (userId) => {
    try {
      logger.info('[USER_MANAGEMENT]', `Attempting to delete user: ${userId}`);

      // Call the actual delete user API
      const result = await deleteUser(userId);

      if (result.success) {
        setSuccess(t('userManagement.userDeleted'));

        // Refresh the user list immediately
        const allUsers = await getAllUsers();
        setUsers(allUsers || []);

        // Update manager and subordinate info for remaining users
        const managerMap = {};
        const subordinatesMap = {};

        if (Array.isArray(allUsers)) {
          for (const user of allUsers) {
            try {
              const manager = await getUserManager(user.id);
              const subordinates = await getUserSubordinates(user.id);

              managerMap[user.id] = manager || null;
              subordinatesMap[user.id] = Array.isArray(subordinates) ? subordinates : [];
            } catch (error) {
              logger.error(`[USER_MANAGEMENT] Error loading hierarchy for user ${user.id}:`, error);
              managerMap[user.id] = null;
              subordinatesMap[user.id] = [];
            }
          }
        }

        setUserManagers(managerMap);
        setUserSubordinates(subordinatesMap);

        logger.info('[USER_MANAGEMENT]', 'User deleted successfully and UI refreshed');
      } else {
        setError(t('userManagement.errorDeleting') + result.message);
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      logger.error('[USER_MANAGEMENT]', 'Error deleting user:', error);
      setError(t('userManagement.errorDeleting') + error.message);
    }
  };

  return (
    <Box key={languageRenderKey}>
      {success && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="success" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">
          {t('userManagement.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('userManagement.registrationRequirement')}
        </Typography>
      </Box>

      {/* Filter status indicator - only show if filters are active */}
      {(roleFilter || managerFilter || departmentFilter) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Chip
            icon={<FilterAltIcon />}
            label={`${t('common.filtered')}: ${roleFilter ? `${t('userManagement.role')}=${getRoleDisplayName(roleFilter)}` : ''} ${managerFilter ? `${t('userManagement.manager')}=${managerFilter === 'none' ? t('userManagement.noManager') : users.find(u => u.id === managerFilter)?.name || managerFilter}` : ''} ${departmentFilter ? `${t('admin.department')}=${departmentFilter === 'none' ? t('admin.noDepartment') : departmentFilter}` : ''}`}
            onDelete={() => {
              setRoleFilter('');
              setManagerFilter('');
              setDepartmentFilter('');
            }}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
      )}

      <Box sx={tableShellSx}>
        <TableContainer component={Paper} sx={responsiveTableContainerSx}>
          <Table sx={{ tableLayout: 'fixed', minWidth: { xs: 980, md: 1140 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={userTableColumnSx.name}>{t('userManagement.nameColumn')}</TableCell>
                <TableCell sx={userTableColumnSx.email}>{t('userManagement.emailColumn')}</TableCell>
                <TableCell sx={userTableColumnSx.role}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={(e) => {
                      setRoleFilterAnchorEl(e.currentTarget);
                    }}>
                    {t('userManagement.roleColumn')}
                    <FilterListIcon fontSize="small" sx={{ ml: 0.5, color: roleFilter ? 'primary.main' : 'action.disabled' }} />
                  </Box>
                </TableCell>
                <TableCell sx={userTableColumnSx.manager}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={(e) => {
                      setManagerFilterAnchorEl(e.currentTarget);
                    }}>
                    {t('userManagement.managerColumn')}
                    <FilterListIcon fontSize="small" sx={{ ml: 0.5, color: managerFilter ? 'primary.main' : 'action.disabled' }} />
                  </Box>
                </TableCell>
                <TableCell sx={userTableColumnSx.department}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={(e) => {
                      setDepartmentFilterAnchorEl(e.currentTarget);
                    }}>
                    {t('admin.department')}
                    <FilterListIcon fontSize="small" sx={{ ml: 0.5, color: departmentFilter ? 'primary.main' : 'action.disabled' }} />
                  </Box>
                </TableCell>
                <TableCell sx={userTableColumnSx.subordinates}>{t('userManagement.subordinatesColumn')}</TableCell>
                <TableCell className="table-actions-cell" sx={userTableColumnSx.actions}>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getFilteredUsers().map((user) => (
                <TableRow key={user.id}>
                  <TableCell sx={userTableColumnSx.name}>{user.name}</TableCell>
                  <TableCell sx={userTableColumnSx.email}>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{user.email}</Typography>
                  </TableCell>
                  <TableCell sx={userTableColumnSx.role}>
                    <Chip
                      label={getRoleDisplayName(user.role)}
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={userTableColumnSx.manager}>
                    {userManagers[user.id] ? (
                      <Tooltip title={userManagers[user.id].name}>
                        <Chip
                          icon={<SupervisorAccountIcon />}
                          label={userManagers[user.id].name.split(' ')[0]}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        icon={<PersonIcon />}
                        label={t('common.none')}
                        size="small"
                        variant="outlined"
                        color="default"
                      />
                    )}
                  </TableCell>
                  <TableCell sx={userTableColumnSx.department}>
                    {user.department ? (
                      <Chip
                        label={user.department}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        label={t('admin.noDepartment')}
                        size="small"
                        variant="outlined"
                        color="default"
                      />
                    )}
                  </TableCell>
                  <TableCell sx={userTableColumnSx.subordinates}>
                    {userSubordinates[user.id] && userSubordinates[user.id].length > 0 ? (
                      <Tooltip title={userSubordinates[user.id].map(sub => sub.name).join(', ')}>
                        <Chip
                          icon={<PeopleIcon />}
                          label={userSubordinates[user.id].length}
                          size="small"
                          color="primary"
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        icon={<PeopleIcon />}
                        label="0"
                        size="small"
                        variant="outlined"
                        color="default"
                      />
                    )}
                  </TableCell>
                  <TableCell className="table-actions-cell" sx={userTableColumnSx.actions}>
                    <Tooltip title={t('userManagement.editUser')}>
                      <IconButton size="small" onClick={() => handleOpenDialog('edit', user)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('userManagement.assignManager')}>
                      <IconButton size="small" onClick={() => handleOpenDialog('manager', user)}>
                        <SupervisorAccountIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('userManagement.deleteUser')}>
                      <IconButton size="small" color="error" onClick={() => handleDeleteUser(user.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? t('userManagement.addUser') :
            dialogMode === 'edit' ? t('userManagement.editUser') :
              t('userManagement.assignManagerFor') + ' ' + selectedUser?.name}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Box sx={{ mb: 2 }}>
              <ErrorState
                error={error}
                message={error}
                severity="error"
                component="UserManagement-Dialog"
                showRetry={false}
              />
            </Box>
          )}

          {dialogMode !== 'manager' && (
            <>
              <TextField
                margin="normal"
                required
                fullWidth
                label={t('userManagement.fullName')}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                label={t('userManagement.emailAddress')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
              />

              {/* No password field needed */}

              <FormControl fullWidth margin="normal">
                <InputLabel>{t('userManagement.role')}</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  label={t('userManagement.role')}
                  onChange={handleInputChange}
                >
                  <MenuItem value={ROLES.USER}>{t('userProfile.roleUser')}</MenuItem>
                  <MenuItem value={ROLES.MANAGER}>{t('userProfile.roleManager')}</MenuItem>
                  <MenuItem value={ROLES.ADMIN}>{t('userProfile.roleAdmin')}</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel>{t('admin.departmentLabel')}</InputLabel>
                <Select
                  name="department"
                  value={formData.department || ''}
                  label={t('admin.departmentLabel')}
                  onChange={handleInputChange}
                >
                  {departmentOptions.map((option) => (
                    <MenuItem key={option.value || 'none'} value={option.value || ''}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {dialogMode === 'manager' && (
            <>
              <Box sx={{ mb: 3, mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('userManagement.assignManagerFor')}: <strong>{selectedUser?.name}</strong>
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <FormControl fullWidth margin="normal">
                  <InputLabel>{t('userManagement.manager')}</InputLabel>
                  <Select
                    name="managerId"
                    value={formData.managerId}
                    label={t('userManagement.manager')}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="">{t('userManagement.noManager')}</MenuItem>
                    {users
                      .filter(u => u.id !== selectedUser?.id) // Can't be own manager
                      .map(user => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.name} ({getRoleDisplayName(user.role)})
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Popovers (following CV/Job analyzer pattern) */}
      {/* Role Filter Menu */}
      <Popover
        open={Boolean(roleFilterAnchorEl)}
        anchorEl={roleFilterAnchorEl}
        onClose={handleRoleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        hideBackdrop={true}
      >
        <Box sx={{ p: 1, minWidth: 200 }}>
          <MenuItem
            value=""
            onClick={() => handleRoleFilterSelect('')}
            sx={{
              fontWeight: roleFilter === '' ? 'bold' : 'normal'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {t('common.all')}
              {roleFilter === '' ?
                <FilterAltIcon fontSize="small" color="primary" /> : null}
            </Box>
          </MenuItem>
          <MenuItem
            value={ROLES.ADMIN}
            onClick={() => handleRoleFilterSelect(ROLES.ADMIN)}
            sx={{ fontWeight: roleFilter === ROLES.ADMIN ? 'bold' : 'normal' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {t('userManagement.roleAdmin')}
              {roleFilter === ROLES.ADMIN ?
                <FilterAltIcon fontSize="small" color="primary" /> : null}
            </Box>
          </MenuItem>
          <MenuItem
            value={ROLES.MANAGER}
            onClick={() => handleRoleFilterSelect(ROLES.MANAGER)}
            sx={{ fontWeight: roleFilter === ROLES.MANAGER ? 'bold' : 'normal' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {t('userManagement.roleManager')}
              {roleFilter === ROLES.MANAGER ?
                <FilterAltIcon fontSize="small" color="primary" /> : null}
            </Box>
          </MenuItem>
          <MenuItem
            value={ROLES.ANALYST}
            onClick={() => handleRoleFilterSelect(ROLES.ANALYST)}
            sx={{ fontWeight: roleFilter === ROLES.ANALYST ? 'bold' : 'normal' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {t('userManagement.roleAnalyst')}
              {roleFilter === ROLES.ANALYST ?
                <FilterAltIcon fontSize="small" color="primary" /> : null}
            </Box>
          </MenuItem>
          <MenuItem
            value={ROLES.USER}
            onClick={() => handleRoleFilterSelect(ROLES.USER)}
            sx={{ fontWeight: roleFilter === ROLES.USER ? 'bold' : 'normal' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {t('userManagement.roleUser')}
              {roleFilter === ROLES.USER ?
                <FilterAltIcon fontSize="small" color="primary" /> : null}
            </Box>
          </MenuItem>
        </Box>
      </Popover>

      {/* Manager Filter Menu */}
      <Popover
        open={Boolean(managerFilterAnchorEl)}
        anchorEl={managerFilterAnchorEl}
        onClose={handleManagerFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        hideBackdrop={true}
      >
        <Box sx={{ p: 1, minWidth: 200 }}>
          <MenuItem
            value=""
            onClick={() => handleManagerFilterSelect('')}
            sx={{
              fontWeight: managerFilter === '' ? 'bold' : 'normal'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {t('common.all')}
              {managerFilter === '' ?
                <FilterAltIcon fontSize="small" color="primary" /> : null}
            </Box>
          </MenuItem>
          <MenuItem
            value="none"
            onClick={() => handleManagerFilterSelect('none')}
            sx={{ fontWeight: managerFilter === 'none' ? 'bold' : 'normal' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {t('userManagement.noManager')}
              {managerFilter === 'none' ?
                <FilterAltIcon fontSize="small" color="primary" /> : null}
            </Box>
          </MenuItem>
          {users
            .filter(user => user.role === ROLES.MANAGER || user.role === ROLES.ADMIN)
            .map(manager => (
              <MenuItem
                key={manager.id}
                value={manager.id}
                onClick={() => handleManagerFilterSelect(manager.id)}
                sx={{ fontWeight: managerFilter === manager.id ? 'bold' : 'normal' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  {manager.name} ({getRoleDisplayName(manager.role)})
                  {managerFilter === manager.id ?
                    <FilterAltIcon fontSize="small" color="primary" /> : null}
                </Box>
              </MenuItem>
            ))
          }
        </Box>
      </Popover>

      {/* Department Filter Menu */}
      <Popover
        open={Boolean(departmentFilterAnchorEl)}
        anchorEl={departmentFilterAnchorEl}
        onClose={handleDepartmentFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        hideBackdrop={true}
      >
        <Box sx={{ p: 1, minWidth: 200 }}>
          <MenuItem
            value=""
            onClick={() => handleDepartmentFilterSelect('')}
            sx={{
              fontWeight: departmentFilter === '' ? 'bold' : 'normal'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {t('common.all')}
              {departmentFilter === '' ?
                <FilterAltIcon fontSize="small" color="primary" /> : null}
            </Box>
          </MenuItem>
          <MenuItem
            value="none"
            onClick={() => handleDepartmentFilterSelect('none')}
            sx={{ fontWeight: departmentFilter === 'none' ? 'bold' : 'normal' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {t('admin.noDepartment')}
              {departmentFilter === 'none' ?
                <FilterAltIcon fontSize="small" color="primary" /> : null}
            </Box>
          </MenuItem>
          {getActiveDepartments().map(department => (
            <MenuItem
              key={department}
              value={department}
              onClick={() => handleDepartmentFilterSelect(department)}
              sx={{ fontWeight: departmentFilter === department ? 'bold' : 'normal' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                {department}
                {departmentFilter === department ?
                  <FilterAltIcon fontSize="small" color="primary" /> : null}
              </Box>
            </MenuItem>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}
